import dynamic from 'next/dynamic';

import { Link } from '@natu/next-link';
import { cn } from '@natu/utils/cn';
import { isArrayWithLength } from '@natu/utils/isArrayWithLength';

import {
  type BlokItem,
  getAssetFromStoryblok,
  getDescriptiveText,
  getLinkPropsFromStoryblok,
  sbEditable,
  type SBProps,
  type StoryblokAsset,
  type StoryblokLink,
} from '../../../../utils';

const ResponsiveImage = dynamic(() =>
  import('@natu/ui/ResponsiveImage').then(mod => mod.ResponsiveImage),
);

const DynamicIcon = dynamic(() => import('@natu/ui/DynamicIcon').then(mod => mod.DynamicIcon));

const DynamicRender = dynamic(() => import('../../../../utils').then(mod => mod.DynamicRender));

const ButtonWithEvent = dynamic(() =>
  import('@natu/ui/Button/ButtonWithEvent').then(mod => mod.ButtonWithEvent),
);

export interface SBOurServicesItemProps {
  image?: StoryblokAsset;
  counter?: string;
  title?: string;
  description?: string;
  list?: BlokItem[];
  buttonLabel?: string;
  buttonUrl?: StoryblokLink;
  variant?: 'image-right' | 'image-left';
}

export const SBOurServicesItem = ({ blok }: SBProps<SBOurServicesItemProps>) => {
  const { buttonLabel, buttonUrl, counter, description, image, list, variant, title } = blok;

  const resolvedImage = getAssetFromStoryblok(image);
  const resolvedButtonUrl = getLinkPropsFromStoryblok(buttonUrl);

  const { descriptiveText } = getDescriptiveText({
    title: buttonLabel,
    link: resolvedButtonUrl,
  });

  return (
    <li
      className={cn(
        'bg-naturaily-azureGray flex flex-col gap-y-8 rounded-[2rem] px-4 py-12 md:items-center md:gap-x-4 lg:gap-x-12 lg:rounded-2xl lg:px-8',
        variant === 'image-left' ? 'md:flex-row' : 'md:flex-row-reverse',
      )}
      {...sbEditable(blok)}
    >
      {resolvedImage.src && (
        <ResponsiveImage
          className="mx-auto w-full max-w-lg md:w-1/2 lg:w-full"
          {...resolvedImage}
        />
      )}
      <div className="md:w-1/2 lg:w-full">
        {counter && <p className="text-primary-700 mb-4 font-bold">{counter}</p>}
        {title && (
          <h2 className="text-display-md-34 xl:text-display-lg-46 mb-4 font-semibold text-gray-900">
            {title}
          </h2>
        )}
        {description && <p className="mb-6 text-lg text-gray-600">{description}</p>}
        {isArrayWithLength(list) && (
          <ul className="mb-8 flex flex-col gap-y-4 lg:mb-12 lg:grid lg:grid-cols-2 lg:gap-4">
            {list.map(item => (
              <li key={item._uid} className="flex gap-x-3.5">
                <DynamicIcon className="shrink-0" icon="circleCheck" />
                <DynamicRender data={item} />
              </li>
            ))}
          </ul>
        )}
        {resolvedButtonUrl && buttonLabel && (
          <ButtonWithEvent
            event="E_cta_to_landing"
            asChild
            aria-label={descriptiveText || buttonLabel}
          >
            <Link
              {...resolvedButtonUrl}
              className="text-[0.813rem] font-semibold uppercase leading-[1.5rem]"
            >
              {buttonLabel}
            </Link>
          </ButtonWithEvent>
        )}
      </div>
    </li>
  );
};
